<template>
  <div class="payment-page" :class="{ 'is-processing': isPaymentProcessing }">
    <div v-if="isPaymentProcessing" class="payment-blocking-overlay">
      <div class="payment-blocking-card">
        <span class="payment-blocking-spinner"></span>
        <p class="payment-blocking-title">Please wait...</p>
        <p class="payment-blocking-sub">Processing your payment request.</p>
        <p class="payment-blocking-sub">Do not refresh or go back.</p>
      </div>
    </div>

    <AppNavbar />

    <main class="container payment-content">
      <!-- PROGRESS -->
      <div class="checkout-progress">
        <div class="progress-step completed">
          <span class="step-dot"><i class="ri-shopping-cart-2-line"></i></span>
          <span class="step-label">Cart</span>
        </div>
        <div class="progress-line completed"></div>
        <div class="progress-step completed">
          <span class="step-dot"><i class="ri-map-pin-line"></i></span>
          <span class="step-label">Address</span>
        </div>
        <div class="progress-line active"></div>
        <div class="progress-step active">
          <span class="step-dot"><i class="ri-bank-card-line"></i></span>
          <span class="step-label">Payment</span>
        </div>
      </div>

      <p v-if="isLoading" class="status-text centered">
        Loading payment details...
      </p>
      <p v-if="!isLoading && errorMessage" class="status-text error centered">
        {{ errorMessage }}
      </p>

      <section v-if="!isLoading" class="checkout-grid">
        <!-- LEFT COL -->
        <!-- PAYMENT OPTIONS -->
        <section class="left-col">
          <div class="panel-card">
            <h2 class="panel-title" style="margin-bottom: 16px">
              Choose Payment Option
            </h2>
            <div class="payment-options">
              <label
                class="option-card"
                v-for="option in paymentOptions"
                :key="option.value"
                :class="{
                  selected: selectedPaymentOption === option.value,
                  disabled: isPaymentProcessing,
                }"
              >
                <input
                  type="radio"
                  name="payment-option"
                  v-model="selectedPaymentOption"
                  :value="option.value"
                  :disabled="isPaymentProcessing"
                />
                <div class="option-icon-wrap">
                  <i :class="option.icon"></i>
                </div>
                <div class="option-text">
                  <p class="option-title">{{ option.label }}</p>
                  <p class="option-sub">{{ option.description }}</p>
                </div>
                <div class="option-radio-col">
                  <div
                    class="radio-ring"
                    :class="{ active: selectedPaymentOption === option.value }"
                  >
                    <i
                      v-if="selectedPaymentOption === option.value"
                      class="ri-check-line check-icon"
                    ></i>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </section>

        <!-- RIGHT COL -->
        <section class="right-col">
          <!-- ORDER SUMMARY -->
          <div class="summary-card">
            <h2 class="col-title">Order Summary</h2>

            <div class="summary-items">
              <div
                v-for="(item, index) in checkoutItems"
                :key="item.key || index"
                class="summary-item"
              >
                <div class="summary-item-img">
                  <img v-if="item.image" :src="item.image" :alt="item.name" />
                  <div v-else class="img-placeholder">
                    <i class="ri-image-line"></i>
                  </div>
                </div>
                <div class="summary-item-info">
                  <p class="summary-item-name">{{ item.name }}</p>
                  <p class="summary-item-sku" v-if="item.sku">
                    SKU: {{ item.sku }}
                  </p>
                  <p class="summary-item-qty">Qty: {{ item.quantity || 1 }}</p>
                  <p
                    class="summary-item-qty"
                    v-if="
                      showCv &&
                      item.coinValue !== undefined &&
                      item.coinValue !== null
                    "
                  >
                    CV: {{ formatNumber(item.coinValue) }}
                  </p>
                </div>
                <!-- <p class="summary-item-price">
                  {{ formatRs(getLineTotal(item)) }}
                </p> -->
              </div>
            </div>

            <div class="summary-totals">
              <div class="total-row">
                <span>Gross Total</span>
                <span>{{ formatRs(orderGrossTotal) }}</span>
              </div>
              <div
                v-for="combo in comboSummary.combos"
                :key="combo.comboId"
                class="total-row combo-discount-row"
              >
                <span>
                  <i class="ri-price-tag-3-fill"></i>
                  Combo Discount &middot; {{ combo.name }}
                </span>
                <span>-{{ formatRs(combo.amount) }}</span>
              </div>
              <div class="total-row">
                <span>Subtotal</span>
                <span>{{ formatRs(orderSubtotal) }}</span>
              </div>
              <div class="total-row">
                <span>Shipping</span>
                <span>{{ formatRs(orderShippingTotal) }}</span>
              </div>
              <div class="total-row" v-if="showCv">
                <span>Total CV</span>
                <span>{{ formatNumber(orderTotalCoinValue) }}</span>
              </div>
              <div class="total-row delivery-row">
                <span class="delivery-estimate">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="1" y="3" width="15" height="13"></rect>
                    <polygon
                      points="16 8 20 8 23 11 23 16 16 16 16 8"
                    ></polygon>
                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                  </svg>
                  Estimated Delivery: <strong>25 - 30 Days</strong>
                </span>
              </div>
              <div class="summary-divider"></div>
              <div class="total-row grand">
                <span>Order Total (Incl. of taxes)</span>
                <strong>{{ formatRs(orderTotalDisplay) }}</strong>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div class="cta-group">
            <router-link
              to="/place-order"
              class="cta-ghost"
              :class="{ disabled: isPaymentProcessing }"
              :tabindex="isPaymentProcessing ? -1 : 0"
              :aria-disabled="isPaymentProcessing ? 'true' : 'false'"
              @click="handleBackToAddressClick"
            >
              <i class="ri-arrow-left-line"></i> Back to Address
            </router-link>
            <button
              type="button"
              class="cta-primary"
              :disabled="isPaymentProcessing || !selectedShippingAddressId"
              @click="handlePlaceOrderAndPayment"
            >
              <i class="ri-lock-line"></i>
              {{
                isPaymentProcessing
                  ? "Processing..."
                  : `Place Order with ${selectedPaymentOptionLabel}`
              }}
            </button>
          </div>
        </section>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as ecomSdk from "rdep-ecom-sdk";
// import {
//   generatePaymentLink,
//   getCustomerAddress,
//   initiateRazorPayPayment,
//   recordOrderPayment,
//   refreshCart,
//   refreshToken,
//   verifyRazorpayStatus,
// } from "rdep-ecom-sdk";
import { useCartStore } from "../../stores/useCartStore.js";
import { getCartComboSummary } from "@/data/comboProducts.js";
// import { useAuthStore } from "../../stores/useAuthStore.js";
// import { useAuthStore } from "../../stores/useAuthStore.js";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const getCustomerAddress = ecomSdk.getCustomerAddress;
const initiateHdfcGatewayPayment =
  ecomSdk.initiateHdfcPayment ||
  ecomSdk.initiateCashfreePayment ||
  ecomSdk.initiateRazorPayPayment ||
  ecomSdk.generatePaymentLink;

const initiateFreechargeGatewayPayment =
  ecomSdk.initiateFreechargePayment ||
  ecomSdk.initiateFreeChargePayment ||
  ecomSdk.initiateAxisFreechargePayment;

// const authStore = useAuthStore();

// const CURRENT_USER_STORAGE_KEY = "auth.currentUser";
// const CART_ID_STORAGE_KEY = "cart_id";
// const CART_META_STORAGE_KEY = "ecom.cart.meta";

const cartStore = useCartStore();
const route = useRoute();
const router = useRouter();
const HDFC_CONTEXT_STORAGE_KEY = "hdfc.payment.context";

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const userRoleToken = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const roleCandidates = [
      user?.userType,
      user?.role,
      user?.loginRole,
      user?.customerType,
    ]
      .map(normalizeRoleToken)
      .filter(Boolean);

    return roleCandidates[0] || "";
  } catch (error) {
    return "";
  }
});

const showCv = computed(() => userRoleToken.value === "DISTRIBUTOR");

const isLoading = ref(false);
const isAddressLoading = ref(false);
const isPaymentProcessing = ref(false);
// const isGeneratingPaymentLink = ref(false);
const errorMessage = ref("");
// const paymentLinkError = ref("");
// const paymentLinkResponse = ref(null);

const addresses = ref([]);
const selectedShippingAddressId = ref(null);
const selectedBillingAddressId = ref(null);

const selectedPaymentOption = ref("freecharge");
const initialCustomerGst = String(route.query?.customerGSTNumber || "").trim();
const customerGSTNumber = ref(
  normalizeCustomerGstNumber(
    initialCustomerGst.toLowerCase() === "null" ? "" : initialCustomerGst,
  ),
);
// const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
// let razorpayScriptPromise = null;

const paymentOptions = [
  {
    value: "freecharge",
    label: "Freecharge",
    description: "Pay securely via Freecharge checkout gateway.",
    icon: "ri-bank-card-line",
  },
  {
    value: "hdfc-1",
    label: "HDFC ",
    description:
      "Pay securely via HDFC payment gateway (preferred for Business Tool and Dinnerware).",
    icon: "ri-money-rupee-circle-line",
  },
  {
    value: "hdfc-2",
    label: "HDFC ",
    description:
      "Pay securely via HDFC payment gateway (preferred for Watches and Jewellery).",
    icon: "ri-money-rupee-circle-line",
  },
];

const selectedPaymentOptionLabel = computed(() => {
  const selected =
    paymentOptions.find(
      (entry) => entry.value === selectedPaymentOption.value,
    ) || paymentOptions[0];
  return selected?.label || "Payment Gateway";
});

const checkoutItems = ref([]);

function normalizeCustomerGstNumber(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 15);
}

const selectedShippingAddress = computed(
  () =>
    addresses.value.find(
      (entry) => entry.id === selectedShippingAddressId.value,
    ) || null,
);

const orderSubtotal = computed(() => Number(cartStore.orderNetTotal) || 0);

const comboSummary = computed(() => getCartComboSummary(cartStore.items));

const orderGrossTotal = computed(() => {
  const gross =
    orderSubtotal.value + Number(comboSummary.value.totalDiscount || 0);
  return Math.round((gross + Number.EPSILON) * 100) / 100;
});

const orderShippingTotal = computed(
  () => Number(cartStore.orderShippingCharge) || 0,
);

const orderTotalDisplay = computed(
  () => Number(cartStore.displayOrderTotal) || 0,
);

const orderTotalCoinValue = computed(() => {
  const totalCoinValue = Number(cartStore.cartMeta?.totalCoinValue);
  return Number.isFinite(totalCoinValue)
    ? totalCoinValue
    : Number(cartStore.grandTotalCoinValue) || 0;
});

let savedBodyOverflow = "";
let isBodyScrollLocked = false;

function setBodyScrollLock(shouldLock) {
  if (typeof document === "undefined") return;
  const body = document.body;
  if (!body) return;

  if (shouldLock && !isBodyScrollLocked) {
    savedBodyOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    isBodyScrollLocked = true;
    return;
  }

  if (!shouldLock && isBodyScrollLocked) {
    body.style.overflow = savedBodyOverflow;
    isBodyScrollLocked = false;
  }
}

watch(
  isPaymentProcessing,
  (isProcessing) => {
    setBodyScrollLock(isProcessing);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  setBodyScrollLock(false);
});

function buildPaymentStatusRoute(orderIdValue, uidValue) {
  const uid = String(uidValue || "").trim();
  const orderId = String(orderIdValue || "").trim();
  const routeTarget = {
    name: "payment-status",
    query: {
      gateway: "freecharge",
    },
  };

  if (uid) {
    routeTarget.params = { uid };
    routeTarget.query.uid = uid;
  }
  if (orderId) {
    routeTarget.query.orderId = orderId;
  }

  return routeTarget;
}

function getOrderIdFromAnyResponse(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const candidate =
    data.orderId ||
    data.order_id ||
    data.orderNo ||
    data.orderNumber ||
    data.id ||
    nested.orderId ||
    nested.order_id ||
    nested.orderNo ||
    nested.orderNumber ||
    nested.id ||
    "";
  return String(candidate || "").trim();
}

// const selectedOrderId = computed(() => {
//   const fromRoute = String(route.query?.orderId || "").trim();
//   if (fromRoute) return fromRoute;
//   return getOrderIdFromAnyResponse(cartStore.lastResponse);
// });

function normalizeAddressForPlaceOrder(address) {
  if (!address || typeof address !== "object") return null;

  return {
    id: null,
    addressId: address.id ?? null,
    addressType: address.addressType || "Home",
    label: address.label || address.addressType || "Home",
    contactPerson: address.contactPerson || address.fullName || "",
    fullName: address.fullName || "",
    phone: address.phone || address.contactPhoneNumber || "",
    contactPhoneNumber: address.contactPhoneNumber || address.phone || "",
    addressLine: address.addressLine || "",
    addressLine1: address.addressLine1 || address.addressLine || "",
    addressLine2: address.addressLine2 || "",
    addressLine3: address.addressLine3 || "",
    city: address.city || "",
    district: address.district || address.city || "",
    state: address.state || "",
    country: address.country || "India",
    pincode: address.pincode || "",
    pinCode: address.pincode || "",
    landmark: address.landmark || "",
    locationUrl: address.locationUrl || "",
    tag: address.tag || address.addressType || "Home",
  };
}

function getApiStatusCode(source) {
  const data =
    source?.response?.data || source?.data || source || source?.response || {};
  const nested = data?.data || {};
  return String(data?.statusCode || nested?.statusCode || "").trim();
}

function getApiStatusMessage(source) {
  const data =
    source?.response?.data || source?.data || source || source?.response || {};
  const nested = data?.data || {};
  return String(data?.statusMessage || nested?.statusMessage || "").trim();
}

function stripWrappedText(value) {
  const wrappers = new Set(['"', "'", "`", "(", ")", "[", "]"]);
  let next = String(value || "").trim();

  while (next.length && wrappers.has(next.charAt(0))) {
    next = next.slice(1).trim();
  }

  while (next.length && wrappers.has(next.charAt(next.length - 1))) {
    next = next.slice(0, -1).trim();
  }

  return next;
}

function normalizeCheckoutUrl(value) {
  const raw = stripWrappedText(value);
  if (!raw) return "";

  const markdownMatch = raw.match(/\((https?:\/\/[^)]+)\)/i);
  const directMatch = raw.match(/https?:\/\/[^\s"')\]]+/i);
  const extracted =
    stripWrappedText(markdownMatch?.[1] || directMatch?.[0] || raw) || "";

  return extracted.replace(/["'`]+$/g, "").trim();
}

function getFreechargeCheckoutPayload(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const checkoutUrl = normalizeCheckoutUrl(
    data.checkoutUrl ||
      nested.checkoutUrl ||
      data.checkoutURL ||
      nested.checkoutURL ||
      data.url ||
      nested.url ||
      "",
  );

  const formDataCandidate =
    data.formData ||
    nested.formData ||
    data.checkoutForm ||
    nested.checkoutForm ||
    null;

  const formData =
    formDataCandidate && typeof formDataCandidate === "object"
      ? Object.entries(formDataCandidate).reduce((acc, [key, value]) => {
          if (!key) return acc;
          acc[key] = String(value ?? "").trim();
          return acc;
        }, {})
      : null;

  return {
    checkoutUrl,
    formData,
    merchantTxnId: String(
      data.merchantTxnId || nested.merchantTxnId || "",
    ).trim(),
  };
}

function submitHostedPaymentForm(checkoutUrl, formData) {
  if (typeof document === "undefined") {
    throw new Error("Unable to open payment checkout outside browser context.");
  }

  const actionUrl = normalizeCheckoutUrl(checkoutUrl);
  if (!actionUrl) {
    throw new Error("Checkout URL is missing in payment response.");
  }

  if (!formData || typeof formData !== "object") {
    throw new Error("Checkout form data is missing in payment response.");
  }

  const entries = Object.entries(formData).filter(([key]) =>
    String(key).trim(),
  );
  if (!entries.length) {
    throw new Error("Checkout form data is empty in payment response.");
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = actionUrl;
  form.style.display = "none";

  for (const [key, value] of entries) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = String(key);
    input.value = String(value ?? "");
    form.appendChild(input);
  }

  (document.body || document.documentElement).appendChild(form);
  form.submit();
}

function getHdfcRedirectUrl(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const normalizeCandidateUrl = (value) => {
    const next = stripWrappedText(value);
    if (!next) return "";

    return next;
  };

  const candidates = [
    data.paymentLink,
    nested.paymentLink,
    data.paymentUrl,
    data.paymentURL,
    data.returnUrl,
    data.returnURL,
    data.redirectUrl,
    data.redirectURL,
    data.url,
    data.gatewayUrl,
    data.gatewayURL,
    data.hdfcUrl,
    nested.paymentUrl,
    nested.paymentURL,
    nested.returnUrl,
    nested.returnURL,
    nested.redirectUrl,
    nested.redirectURL,
    nested.url,
    nested.gatewayUrl,
    nested.gatewayURL,
    nested.hdfcUrl,
  ];

  return (
    candidates
      .map(normalizeCandidateUrl)
      .find((entry) => /^https?:\/\//i.test(entry)) || ""
  );
}

function extractUidFromUrlLike(value) {
  const raw = stripWrappedText(value);
  if (!raw) return "";

  const candidates = [raw];
  try {
    candidates.push(decodeURIComponent(raw));
  } catch (error) {
    // ignore decode failures
  }

  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      const uid = stripWrappedText(
        parsed.searchParams.get("uid") ||
          parsed.searchParams.get("UID") ||
          parsed.searchParams.get("paymentUid") ||
          parsed.searchParams.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    } catch (error) {
      const qIndex = candidate.indexOf("?");
      if (qIndex < 0) {
        continue;
      }

      const params = new URLSearchParams(candidate.slice(qIndex + 1));
      const uid = stripWrappedText(
        params.get("uid") ||
          params.get("UID") ||
          params.get("paymentUid") ||
          params.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    }
  }

  return "";
}

function getHdfcUid(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const fromBody = stripWrappedText(
    data.uid ||
      nested.uid ||
      data.paymentUid ||
      nested.paymentUid ||
      data.referenceId ||
      nested.referenceId ||
      "",
  );
  if (fromBody) return fromBody;

  return extractUidFromUrlLike(data.returnUrl || data.returnURL || "") || "";
}

function saveHdfcContext(context) {
  try {
    window.localStorage.setItem(
      HDFC_CONTEXT_STORAGE_KEY,
      JSON.stringify(context || {}),
    );
  } catch (error) {
    // no-op
  }
}

function normalizeAddressesFromResponse(response) {
  const root = response?.data || response || {};
  const nested = root?.data || {};

  const list =
    (Array.isArray(root) ? root : null) ||
    root.addresses ||
    root.addressList ||
    root.customerAddresses ||
    root.items ||
    nested.addresses ||
    nested.addressList ||
    nested.customerAddresses ||
    nested.items ||
    [];

  if (!Array.isArray(list)) return [];

  return list.map((entry, index) => {
    const rawId = entry?.id ?? entry?.addressId ?? entry?.customerAddressId;
    const parsedId = Number(rawId);
    const nextId =
      Number.isFinite(parsedId) && parsedId > 0 ? parsedId : index + 1;

    const typeRaw = String(
      entry?.tag || entry?.addressType || entry?.type || "Home",
    ).trim();
    const addressType =
      typeRaw.toLowerCase() === "office"
        ? "Office"
        : typeRaw.toLowerCase() === "other"
        ? "Other"
        : "Home";

    const addressLabel = String(
      entry?.label || entry?.tag || addressType,
    ).trim();

    const isDefaultRaw =
      entry?.isDefault ??
      entry?.defaultDeliveryAddress ??
      entry?.defaultAddress ??
      entry?.is_default ??
      entry?.default;

    const addressLine =
      entry?.addressLine ||
      entry?.address ||
      [entry?.addressLine1, entry?.addressLine2, entry?.addressLine3]
        .filter(Boolean)
        .join(", ") ||
      entry?.address1 ||
      entry?.line1 ||
      "";

    const isDefault =
      isDefaultRaw === true ||
      isDefaultRaw === 1 ||
      isDefaultRaw === "1" ||
      String(isDefaultRaw || "").toUpperCase() === "Y" ||
      String(isDefaultRaw || "").toLowerCase() === "true";

    return {
      id: nextId,
      label: addressLabel,
      tag: entry?.tag || addressType,
      addressType,
      fullName:
        entry?.fullName ||
        entry?.name ||
        entry?.customerName ||
        entry?.contactPerson ||
        "",
      phone:
        entry?.phone ||
        entry?.mobile ||
        entry?.mobileNumber ||
        entry?.customerMobile ||
        entry?.customerMobileNumber ||
        entry?.contactPhoneNumber ||
        "",
      contactPhoneNumber:
        entry?.contactPhoneNumber ||
        entry?.phone ||
        entry?.mobile ||
        entry?.mobileNumber ||
        "",
      addressLine1:
        entry?.addressLine1 || entry?.address1 || entry?.line1 || "",
      addressLine2:
        entry?.addressLine2 || entry?.address2 || entry?.line2 || "",
      addressLine3:
        entry?.addressLine3 || entry?.address3 || entry?.line3 || "",
      addressLine,
      city: entry?.city || entry?.district || "",
      district: entry?.district || entry?.city || "",
      state: entry?.state || "",
      country: entry?.country || "India",
      landmark: entry?.landmark || "",
      locationUrl: entry?.locationUrl || "",
      pincode:
        entry?.pincode ||
        entry?.pinCode ||
        entry?.postalCode ||
        entry?.zipCode ||
        "",
      isDefault,
      fromApi: true,
    };
  });
}

async function loadCustomerAddresses() {
  isAddressLoading.value = true;
  try {
    const response = await getCustomerAddress();
    const apiAddresses = normalizeAddressesFromResponse(response);

    addresses.value = apiAddresses;
    const routeShippingId = Number(route.query?.shippingAddressId || 0) || null;
    const routeBillingId = Number(route.query?.billingAddressId || 0) || null;
    const routeShippingAddress = routeShippingId
      ? addresses.value.find((entry) => entry.id === routeShippingId) || null
      : null;
    const selectedShipping =
      routeShippingAddress ||
      addresses.value.find((entry) => entry.isDefault) ||
      addresses.value[0] ||
      null;
    selectedShippingAddressId.value = selectedShipping?.id || null;

    const routeBillingAddress = routeBillingId
      ? addresses.value.find((entry) => entry.id === routeBillingId) || null
      : null;
    selectedBillingAddressId.value =
      routeBillingAddress?.id || selectedShippingAddressId.value || null;
  } catch (error) {
    // no-op
  } finally {
    isAddressLoading.value = false;
  }
}

// async function refreshPaymentLinkForSelectedOption(orderId) {
//   if (!selectedPaymentOption.value) {
//     paymentLinkError.value = "";
//     paymentLinkResponse.value = null;
//     return "";
//   }

//   const targetOrderId = String(orderId || selectedOrderId.value || "").trim();

//   isGeneratingPaymentLink.value = true;
//   paymentLinkError.value = "";
//   try {
//     const response = await generatePaymentLink(targetOrderId);
//     paymentLinkResponse.value = response || null;

//     const smsLink = String(
//       response?.smsLink || response?.data?.smsLink || "",
//     ).trim();
//     if (!smsLink) {
//       throw new Error(
//         "Payment link was generated but smsLink is missing in response.",
//       );
//     }

//     return smsLink;
//   } catch (error) {
//     paymentLinkResponse.value = null;
//     paymentLinkError.value =
//       error?.response?.data?.statusMessage ||
//       error?.message ||
//       "Failed to generate payment link.";
//     throw new Error(paymentLinkError.value);
//   } finally {
//     isGeneratingPaymentLink.value = false;
//   }
// }

function normalizeItemsFromResponse(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const list =
    data.cartItems ||
    data.items ||
    data.item_list ||
    data.cart?.item_list ||
    nested.cartItems ||
    nested.items ||
    nested.item_list ||
    nested.cart?.item_list ||
    [];
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => ({
    key: item.productId || item.id || item.sku || index,
    name: item.name || item.product?.name || "Product",
    sku: item.sku || item.product?.sku || "",
    quantity: Number(item.quantity || item.qty || item.itemQty || 1) || 1,
    price:
      Number(
        item.netPrice ??
          item.net_price ??
          item.price ??
          item.listPrice ??
          item.mrp ??
          0,
      ) || 0,
    mrp: Number(item.mrp ?? item.price ?? item.listPrice ?? 0) || 0,
    coinValue:
      Number(item.coinValue ?? item.coin_value ?? item.totalCoinValue ?? 0) ||
      0,
    shippingCharge:
      Number(item.shippingCharge ?? item.shipping_charge ?? 0) || 0,
    productId: item.productId || item.id || "",
    storeCode: item.storeCode || item.store_code || item.store?.storeCode || "",
    storeId:
      Number(item.storeId || item.store_id || item.store?.id || 0) || undefined,
  }));
}

// function getLineTotal(item) {
//   return (Number(item?.quantity || 1) || 1) * (Number(item?.price || 0) || 0);
// }

function formatRs(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "₹0";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(parsed)}`;
}

function formatNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(parsed);
}

function handleBackToAddressClick(event) {
  if (!isPaymentProcessing.value) return;
  event?.preventDefault?.();
}

// function loadRazorpayCheckoutScript() {
//   if (typeof window === "undefined") {
//     return Promise.reject(new Error("Razorpay requires browser context."));
//   }

//   if (window.Razorpay) {
//     return Promise.resolve();
//   }

//   if (razorpayScriptPromise) {
//     return razorpayScriptPromise;
//   }

//   razorpayScriptPromise = new Promise((resolve, reject) => {
//     let isSettled = false;
//     const settle = (handler, value) => {
//       if (isSettled) return;
//       isSettled = true;
//       handler(value);
//     };

//     const onLoad = () => {
//       if (window.Razorpay) {
//         settle(resolve);
//         return;
//       }

//       settle(
//         reject,
//         new Error("Razorpay script loaded but checkout is unavailable."),
//       );
//     };

//     const onError = () => {
//       settle(reject, new Error("Failed to load Razorpay checkout script."));
//     };

//     const existing = document.querySelector(
//       `script[src="${RAZORPAY_CHECKOUT_SRC}"]`,
//     );

//     if (existing) {
//       if (window.Razorpay) {
//         settle(resolve);
//         return;
//       }

//       existing.addEventListener("load", onLoad, { once: true });
//       existing.addEventListener("error", onError, { once: true });

//       // If the script had already finished loading before listeners were attached,
//       // resolve/reject quickly based on Razorpay global availability.
//       window.setTimeout(() => {
//         if (window.Razorpay) {
//           settle(resolve);
//           return;
//         }
//         onError();
//       }, 1200);
//       return;
//     }

//     const script = document.createElement("script");
//     script.src = RAZORPAY_CHECKOUT_SRC;
//     script.async = true;
//     script.onload = onLoad;
//     script.onerror = onError;
//     (document.head || document.body || document.documentElement).appendChild(
//       script,
//     );
//   }).finally(() => {
//     if (window.Razorpay) return;
//     razorpayScriptPromise = null;
//   });

//   return razorpayScriptPromise;
// }

// function getRazorpayCheckoutConfig(response, fallbackOrderId, fallbackAmount) {
//   const data = response?.data || response || {};
//   const nested = data?.data || {};
//   const payload =
//     data.razorpay ||
//     data.checkout ||
//     data.orderData ||
//     nested.razorpay ||
//     nested.checkout ||
//     nested.orderData ||
//     {};

//   const key = String(
//     payload.key ||
//       payload.key_id ||
//       data.key ||
//       data.keyId ||
//       data.razorpayKey ||
//       data.razorpayKeyId ||
//       nested.key ||
//       nested.keyId ||
//       nested.razorpayKey ||
//       nested.razorpayKeyId ||
//       import.meta.env.VUE_APP_RAZORPAY_KEY_ID ||
//       "",
//   ).trim();

//   const orderId = String(
//     payload.order_id ||
//       payload.orderId ||
//       payload.id ||
//       data.order_id ||
//       data.orderId ||
//       data.id ||
//       nested.order_id ||
//       nested.orderId ||
//       nested.id ||
//       fallbackOrderId ||
//       "",
//   ).trim();

//   const amount =
//     Number(
//       payload.amount ||
//         payload.amountInPaise ||
//         data.amount ||
//         data.amountInPaise ||
//         nested.amount ||
//         nested.amountInPaise ||
//         0,
//     ) || Math.round((Number(fallbackAmount || 0) || 0) * 100);

//   const currency = String(
//     payload.currency || data.currency || nested.currency || "INR",
//   ).trim();

//   return {
//     key,
//     orderId,
//     amount,
//     currency,
//     name: String(payload.name || data.name || "IndieKonnect").trim(),
//     description: String(
//       payload.description || data.description || "Order payment",
//     ).trim(),
//     image: String(payload.image || data.image || "").trim(),
//     prefill: payload.prefill || data.prefill || nested.prefill || {},
//     notes: payload.notes || data.notes || nested.notes || {},
//     theme: payload.theme || data.theme || nested.theme || { color: "#2563eb" },
//   };
// }

// function getRazorpayHostedPaymentLink(response) {
//   const data = response?.data || response || {};
//   const nested = data?.data || {};
//   const payload = data?.razorpay || nested?.razorpay || {};

//   const candidates = [
//     payload.short_url,
//     payload.shortUrl,
//     payload.payment_link,
//     payload.paymentLink,
//     payload.payment_url,
//     payload.paymentUrl,
//     payload.checkout_url,
//     payload.checkoutUrl,
//     payload.redirect_url,
//     payload.redirectUrl,
//     data.short_url,
//     data.shortUrl,
//     data.payment_link,
//     data.paymentLink,
//     data.payment_url,
//     data.paymentUrl,
//     data.checkout_url,
//     data.checkoutUrl,
//     data.redirect_url,
//     data.redirectUrl,
//     nested.short_url,
//     nested.shortUrl,
//     nested.payment_link,
//     nested.paymentLink,
//     nested.payment_url,
//     nested.paymentUrl,
//     nested.checkout_url,
//     nested.checkoutUrl,
//     nested.redirect_url,
//     nested.redirectUrl,
//   ];

//   return (
//     candidates
//       .map((entry) => String(entry || "").trim())
//       .find((entry) => /^https?:\/\//i.test(entry)) || ""
//   );
// }

// function isRazorpayVerificationSuccessful(response) {
//   const data = response?.data || response || {};
//   const nested = data?.data || {};
//   const statusCode = String(
//     data?.statusCode || nested?.statusCode || "",
//   ).trim();
//   const statusText = String(
//     data?.status ||
//       data?.paymentStatus ||
//       data?.transactionStatus ||
//       nested?.status ||
//       nested?.paymentStatus ||
//       nested?.transactionStatus ||
//       "",
//   )
//     .trim()
//     .toLowerCase();

//   if (statusCode === "200") return true;

//   return ["success", "verified", "paid", "completed"].includes(statusText);
// }

// function openRazorpayCheckout(config, createdOrderId) {
//   if (!window.Razorpay) {
//     throw new Error("Razorpay checkout is not available.");
//   }

//   if (!config.key) {
//     throw new Error("Razorpay checkout configuration is incomplete.");
//   }

//   const orderIdForVerification = String(createdOrderId || "").trim();
//   if (!orderIdForVerification) {
//     throw new Error("Created order id is missing for Razorpay verification.");
//   }

//   const razorpayCheckoutOrderId = String(config.orderId || "").trim();
//   if (!razorpayCheckoutOrderId) {
//     throw new Error("Razorpay checkout configuration is incomplete.");
//   }

//   const options = {
//     key: config.key,
//     amount: String(Math.max(0, Number(config.amount || 0))),
//     currency: config.currency || "INR",
//     name: config.name,
//     description: config.description,
//     image: config.image || undefined,
//     order_id: razorpayCheckoutOrderId,
//     prefill: config.prefill,
//     notes: config.notes,
//     theme: config.theme,
//     handler: async (response) => {
//       isPaymentProcessing.value = true;
//       errorMessage.value = "";
//       try {
//         const razorpayPaymentId = String(
//           response?.razorpay_payment_id || "",
//         ).trim();
//         const razorpayOrderId = String(
//           response?.razorpay_order_id || "",
//         ).trim();
//         const razorpaySignature = String(
//           response?.razorpay_signature || "",
//         ).trim();

//         const verifyResponse = await verifyRazorpayStatus({
//           orderId: orderIdForVerification,
//           razorpayPaymentId,
//           razorpayOrderId,
//           razorpaySignature,
//         });

//         if (!isRazorpayVerificationSuccessful(verifyResponse)) {
//           throw new Error(
//             verifyResponse?.statusMessage ||
//               verifyResponse?.data?.statusMessage ||
//               "Razorpay payment verification failed.",
//           );
//         }

//         await cartStore.clearCartAfterPayment();
//         cartStore.$reset();

//         window.localStorage.removeItem(CART_ID_STORAGE_KEY);
//         window.localStorage.removeItem(CART_META_STORAGE_KEY);
//         checkoutItems.value = [];

//         if (!authStore.auth?.firstOrderCompleted) {
//           const refreshResponse = await refreshToken();
//           const nextToken = authStore.syncAuthTokenFromSource(refreshResponse);
//           const currentUser = authStore.getUser() || authStore.auth || {};
//           const refreshPayload = refreshResponse?.data || refreshResponse || {};
//           const mergedUser = {
//             ...currentUser,
//             ...(typeof refreshPayload === "object" ? refreshPayload : {}),
//           };

//           if (!mergedUser.token && nextToken) {
//             mergedUser.token = nextToken;
//           }

//           authStore.auth = mergedUser;
//           window.localStorage.setItem(
//             CURRENT_USER_STORAGE_KEY,
//             JSON.stringify(mergedUser),
//           );
//         }

//         await router.push({
//           name: "payment-status",
//           query: {
//             orderId: orderIdForVerification,
//           },
//         });
//       } catch (error) {
//         errorMessage.value =
//           error?.response?.data?.statusMessage ||
//           error?.message ||
//           "Payment succeeded but verification failed.";
//         isPaymentProcessing.value = false;
//       }
//     },
//     modal: {
//       ondismiss: () => {
//         isPaymentProcessing.value = false;
//       },
//     },
//   };

//   const checkout = new window.Razorpay(options);
//   checkout.on("payment.failed", (response) => {
//     errorMessage.value =
//       response?.error?.description ||
//       response?.error?.reason ||
//       "Razorpay payment failed. Please try again.";
//     isPaymentProcessing.value = false;
//   });

//   checkout.open();
// }

async function loadCheckoutData() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    await cartStore.syncGuestCartAfterLogin();
    await cartStore.refreshCartData();
    const cartId = Number(cartStore.cartMeta?.cartId || 0);
    if (!cartId) {
      checkoutItems.value = [...(cartStore.items || [])];
      throw new Error("Cart not found. Please add products and try again.");
    }
    const response = await cartStore.refreshCartData();
    const apiItems = normalizeItemsFromResponse(response);
    checkoutItems.value = apiItems.length
      ? apiItems
      : [...(cartStore.items || [])];
  } catch (error) {
    checkoutItems.value = [...(cartStore.items || [])];
    errorMessage.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to load checkout details.";
  } finally {
    isLoading.value = false;
  }
}

// async function handlePaymentContinue() {
//   if (selectedPaymentOption.value === "razorpay") {
//     await handleRazorpayClick();
//     return;
//   }

//   if (isPaymentProcessing.value) return;
//   if (!selectedPaymentOption.value) {
//     selectedPaymentOption.value = "razorpay";
//   }
//   isPaymentProcessing.value = true;
//   errorMessage.value = "";
//   try {
//     const shippingAddress = normalizeAddressForPlaceOrder(
//       selectedShippingAddress.value,
//     );

//     const billingAddress =
//       normalizeAddressForPlaceOrder(
//         addresses.value.find(
//           (entry) => entry.id === selectedBillingAddressId.value,
//         ) || null,
//       ) || shippingAddress;

//     const placedOrder = await cartStore.placeOrderFromCart({
//       shippingAddress,
//       billingAddress,
//     });

//     const createdOrderId =
//       String(placedOrder?.orderId || "").trim() ||
//       getOrderIdFromAnyResponse(placedOrder?.response) ||
//       getOrderIdFromAnyResponse(cartStore.lastResponse);

//     if (selectedPaymentOption.value === "mswipe") {
//       const existingSmsLink = String(
//         paymentLinkResponse.value?.smsLink || "",
//       ).trim();

//       const smsLink =
//         existingSmsLink ||
//         (await refreshPaymentLinkForSelectedOption(createdOrderId));
//       if (!smsLink) {
//         throw new Error("Unable to generate payment link. Please try again.");
//       }

//       // For link-based payments, wait for gateway callback to bring user back.
//       window.location.assign(smsLink);
//       return;
//     }

//     const selectedOption =
//       paymentOptions.find(
//         (option) => option.value === selectedPaymentOption.value,
//       ) || null;
//     const paymentMode = selectedOption?.label || selectedPaymentOption.value;
//     const amount = Number(orderSubtotal.value || 0);
//     const nowIso = new Date().toISOString();
//     const transactionId = `${Date.now()}${Math.floor(Math.random() * 1000)
//       .toString()
//       .padStart(3, "0")}`;

//     const paymentRequest = {
//       sessionId: transactionId,
//       billAmount: amount,
//       orderId: selectedOrderId.value,
//       task: "POS_PAYMENT",
//       timestamp: nowIso,
//       userType: "SELF_CHECKOUT_USER",
//       paymentTransactions: [
//         {
//           amount,
//           paymentChannel: paymentMode,
//           paymentMode,
//           transactionId,
//           paymentChannelId: 1,
//         },
//       ],
//     };

//     const paymentResponse = await recordOrderPayment(paymentRequest);
//     const paymentStatusCode = String(
//       paymentResponse?.statusCode || paymentResponse?.data?.statusCode || "",
//     );

//     if (paymentStatusCode && paymentStatusCode !== "200") {
//       throw new Error(
//         paymentResponse?.statusMessage ||
//           paymentResponse?.data?.statusMessage ||
//           "Payment API returned an unsuccessful status.",
//       );
//     }

//     await cartStore.clearCartAfterPayment();
//     checkoutItems.value = [];
//     await router.push({
//       name: "home",
//       query: {
//         orderId: String(selectedOrderId.value || "").trim(),
//       },
//     });
//   } catch (error) {
//     const apiStatusCode = getApiStatusCode(error);
//     const apiStatusMessage = getApiStatusMessage(error);
//     if (apiStatusCode === "400" && apiStatusMessage) {
//       errorMessage.value = apiStatusMessage;
//       return;
//     }

//     errorMessage.value =
//       error?.response?.data?.statusMessage ||
//       error?.message ||
//       "Payment completed, but failed to refresh cart.";
//   } finally {
//     isPaymentProcessing.value = false;
//   }
// }

// async function handleRazorpayClick() {
//   if (isPaymentProcessing.value) return;

//   selectedPaymentOption.value = "razorpay";
//   isPaymentProcessing.value = true;
//   errorMessage.value = "";

//   try {
//     const shippingAddress = normalizeAddressForPlaceOrder(
//       selectedShippingAddress.value,
//     );

//     const billingAddress =
//       normalizeAddressForPlaceOrder(
//         addresses.value.find(
//           (entry) => entry.id === selectedBillingAddressId.value,
//         ) || null,
//       ) || shippingAddress;

//     const placedOrder = await cartStore.placeOrderFromCart({
//       shippingAddress,
//       billingAddress,
//     });

//     const createdOrderId =
//       String(placedOrder?.orderId || "").trim() ||
//       getOrderIdFromAnyResponse(placedOrder?.response) ||
//       getOrderIdFromAnyResponse(cartStore.lastResponse);

//     if (!createdOrderId) {
//       throw new Error("Unable to create order for Razorpay payment.");
//     }

//     const razorpayResponse = await initiateRazorPayPayment(createdOrderId);
//     const razorpayStatusCode = getApiStatusCode(razorpayResponse);
//     const razorpayStatusMessage = getApiStatusMessage(razorpayResponse);
//     if (razorpayStatusCode === "400") {
//       throw new Error(
//         razorpayStatusMessage || "Unable to initiate Razorpay payment.",
//       );
//     }

//     const hostedPaymentLink = getRazorpayHostedPaymentLink(razorpayResponse);

//     try {
//       await loadRazorpayCheckoutScript();
//     } catch (scriptLoadError) {
//       if (hostedPaymentLink) {
//         window.location.assign(hostedPaymentLink);
//         return;
//       }
//       throw scriptLoadError;
//     }

//     const checkoutConfig = getRazorpayCheckoutConfig(
//       razorpayResponse,
//       createdOrderId,
//       orderSubtotal.value + orderShippingTotal.value,
//     );
//     const hasPopupConfig =
//       Boolean(String(checkoutConfig.key || "").trim()) &&
//       Boolean(String(checkoutConfig.orderId || "").trim());

//     if (!hasPopupConfig && hostedPaymentLink) {
//       window.location.assign(hostedPaymentLink);
//       return;
//     }

//     try {
//       openRazorpayCheckout(checkoutConfig, createdOrderId);
//     } catch (checkoutError) {
//       if (hostedPaymentLink) {
//         window.location.assign(hostedPaymentLink);
//         return;
//       }
//       throw checkoutError;
//     }
//   } catch (error) {
//     const apiStatusCode = getApiStatusCode(error);
//     const apiStatusMessage = getApiStatusMessage(error);
//     if (apiStatusCode === "400" && apiStatusMessage) {
//       errorMessage.value = apiStatusMessage;
//       return;
//     }

//     const failureMessage = String(error?.message || "");
//     if (failureMessage.includes("Failed to load Razorpay checkout script.")) {
//       errorMessage.value =
//         "Unable to open Razorpay popup. Please disable ad blockers or allow checkout.razorpay.com, then try again.";
//       return;
//     }

//     if (
//       failureMessage.includes("Razorpay checkout configuration is incomplete.")
//     ) {
//       errorMessage.value =
//         "Unable to start Razorpay payment. Please try again.";
//       return;
//     }

//     errorMessage.value =
//       error?.response?.data?.statusMessage ||
//       error?.message ||
//       "Failed to start Razorpay payment.";
//   } finally {
//     isPaymentProcessing.value = false;
//   }
// }

async function handlePlaceOrderAndPayment() {
  if (isPaymentProcessing.value) return;

  let hasNavigatedAway = false;
  let selectedGateway = "hdfc";
  let createdOrderId = "";
  let freechargeTxnUid = "";
  isPaymentProcessing.value = true;
  errorMessage.value = "";

  try {
    if (!selectedPaymentOption.value) {
      selectedPaymentOption.value = "freecharge";
    }

    selectedGateway = String(selectedPaymentOption.value || "").trim();
    const isHdfc = ["hdfc-1", "hdfc-2"].includes(selectedGateway);
    const isFreecharge = selectedGateway === "freecharge";
    if (!isHdfc && !isFreecharge) {
      throw new Error("Please select a valid payment option.");
    }

    const shippingAddress = normalizeAddressForPlaceOrder(
      selectedShippingAddress.value,
    );

    if (!shippingAddress) {
      throw new Error("Please select a shipping address before placing order.");
    }

    const billingAddress =
      normalizeAddressForPlaceOrder(
        addresses.value.find(
          (entry) => entry.id === selectedBillingAddressId.value,
        ) || null,
      ) || shippingAddress;

    const placedOrder = await cartStore.placeOrderFromCart({
      shippingAddress,
      billingAddress,
      customerGSTNumber: normalizeCustomerGstNumber(customerGSTNumber.value),
    });

    createdOrderId =
      String(placedOrder?.orderId || "").trim() ||
      getOrderIdFromAnyResponse(placedOrder?.response) ||
      getOrderIdFromAnyResponse(cartStore.lastResponse);

    if (!createdOrderId) {
      throw new Error("Order placed but orderId is missing in response.");
    }

    if (isHdfc) {
      if (typeof initiateHdfcGatewayPayment !== "function") {
        throw new Error("HDFC payment initiation is not available in the SDK.");
      }

      const hdfcInitResponse = await initiateHdfcGatewayPayment(
        createdOrderId,
        selectedGateway,
      );
      const statusCode = getApiStatusCode(hdfcInitResponse);
      if (statusCode && statusCode !== "200") {
        throw new Error(
          getApiStatusMessage(hdfcInitResponse) ||
            "Failed to initiate HDFC payment.",
        );
      }

      const hdfcUid = getHdfcUid(hdfcInitResponse);
      const redirectUrl = getHdfcRedirectUrl(hdfcInitResponse);
      saveHdfcContext({
        orderId: createdOrderId,
        uid: hdfcUid,
        returnUrl: redirectUrl,
        source: "initiate-hdfc",
        createdAt: new Date().toISOString(),
      });

      if (!redirectUrl) {
        throw new Error(
          "HDFC returnUrl is missing in payment initiation response.",
        );
      }

      hasNavigatedAway = true;
      window.location.assign(redirectUrl);
      return;
    }

    if (typeof initiateFreechargeGatewayPayment !== "function") {
      throw new Error(
        "Freecharge payment initiation is not available in the SDK.",
      );
    }

    const freechargeInitResponse = await initiateFreechargeGatewayPayment(
      createdOrderId,
    );
    const freechargeStatusCode = getApiStatusCode(freechargeInitResponse);
    if (freechargeStatusCode && freechargeStatusCode !== "200") {
      throw new Error(
        getApiStatusMessage(freechargeInitResponse) ||
          "Failed to initiate Freecharge payment.",
      );
    }

    const { checkoutUrl, formData, merchantTxnId } =
      getFreechargeCheckoutPayload(freechargeInitResponse);
    freechargeTxnUid = merchantTxnId;

    saveHdfcContext({
      orderId: createdOrderId,
      uid: merchantTxnId,
      returnUrl: checkoutUrl,
      source: "initiate-freecharge",
      createdAt: new Date().toISOString(),
    });

    hasNavigatedAway = true;
    submitHostedPaymentForm(checkoutUrl, formData);
    return;
  } catch (error) {
    const failureMessage =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to place order and initiate payment.";

    errorMessage.value = failureMessage;

    if (
      selectedGateway === "freecharge" &&
      String(failureMessage).toLowerCase().includes("internal server error")
    ) {
      router
        .push(buildPaymentStatusRoute(createdOrderId, freechargeTxnUid))
        .catch(() => {});
    }
  } finally {
    if (!hasNavigatedAway) {
      isPaymentProcessing.value = false;
    }
  }
}

onMounted(async () => {
  await Promise.all([loadCheckoutData(), loadCustomerAddresses()]);
});
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.payment-page {
  min-height: 100vh;
  background: #f8fafc;
}

.payment-page.is-processing {
  user-select: none;
}

.payment-blocking-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(10, 34, 64, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.payment-blocking-card {
  width: min(92vw, 360px);
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(0, 61, 165, 0.14);
  box-shadow: 0 14px 34px rgba(10, 34, 64, 0.2);
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.payment-blocking-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 3px solid #dbe7f8;
  border-top-color: #003da5;
  animation: payment-spin 0.75s linear infinite;
}

.payment-blocking-title {
  margin: 10px 0 2px;
  font-size: 15px;
  font-weight: 800;
  color: #0a2240;
}

.payment-blocking-sub {
  margin: 0;
  font-size: 12px;
  color: #5a6b82;
}

@keyframes payment-spin {
  to {
    transform: rotate(360deg);
  }
}

.container {
  width: 92%;
  max-width: 1200px;
  margin: 0 auto;
}

.payment-content {
  padding: 32px 0 64px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* ── PROGRESS ── */
.checkout-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 8px 0 4px;
}
.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.step-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #d1dbe8;
  background: #fff;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.progress-step.completed .step-dot {
  border-color: #003da5;
  background: #003da5;
  color: #fff;
}
.progress-step.active .step-dot {
  border-color: #003da5;
  color: #003da5;
  box-shadow: 0 0 0 4px rgba(0, 61, 165, 0.1);
}
.step-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}
.progress-step.completed .step-label,
.progress-step.active .step-label {
  color: #003da5;
}
.progress-line {
  width: 80px;
  height: 2px;
  background: #d1dbe8;
  margin-bottom: 18px;
}
.progress-line.completed,
.progress-line.active {
  background: #003da5;
}

/* ── GRID ── */
.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
}
.left-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── PANEL CARDS ── */
.panel-card,
.summary-card {
  background: #fff;
  border: 1px solid rgba(0, 61, 165, 0.08);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 61, 165, 0.02);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.panel-title,
.col-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0a2240;
  letter-spacing: -0.3px;
}

.payment-order-id {
  margin-top: 12px;
}

.payment-response-box {
  margin-top: 12px;
  border: 1px solid rgba(0, 61, 165, 0.12);
  border-radius: 10px;
  padding: 12px;
  background: #f8fbff;
}

.payment-response-box pre {
  margin: 8px 0 0;
  max-height: 220px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.4;
  color: #0a2240;
}
.sub-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
}

/* ── SHIPPING ADDRESS DISPLAY ── */
.selected-address-card {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.addr-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.addr-left-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.addr-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 61, 165, 0.06);
  color: #003da5;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
.selected-pill {
  font-size: 11px;
  font-weight: 700;
  color: #0a2240;
  background: #ffc72c;
  border-radius: 999px;
  padding: 3px 9px;
}
.addr-right-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.icon-action-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
  color: #5a6b82;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.18s;
  font-size: 15px;
}
.icon-action-btn:hover {
  border-color: #003da5;
  color: #003da5;
  background: rgba(0, 61, 165, 0.05);
}
.icon-action-btn.danger:hover {
  border-color: #dc2626;
  color: #dc2626;
  background: #fff4f4;
}

.addr-body {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.addr-check-col {
  flex-shrink: 0;
  padding-top: 2px;
}
.radio-ring {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d1dbe8;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s;
  background: #fff;
}
.radio-ring.active {
  border-color: #003da5;
  background: #003da5;
}
.check-icon {
  font-size: 12px;
  color: #fff;
  font-weight: 700;
}

.addr-text-col {
  flex: 1;
  min-width: 0;
}
.address-name {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
}
.address-line {
  margin: 0 0 3px;
  font-size: 13px;
  color: #344760;
  line-height: 1.5;
}
.address-meta {
  margin: 0;
  font-size: 13px;
  color: #5a6b82;
}
.phone-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
}
.phone-meta i {
  font-size: 13px;
}

/* ── PAYMENT OPTIONS ── */
.payment-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gstin-field-wrap {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gstin-label {
  margin: 0;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #0a2240;
}

.gstin-input {
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid #d1dbe8;
  border-radius: 10px;
  height: 42px;
  padding: 0 12px;
  font-size: 14px;
  color: #0a2240;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.gstin-input:focus {
  outline: none;
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.12);
}

.gstin-input:disabled {
  background: #f4f7fb;
  cursor: not-allowed;
}
.option-card {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1.5px solid #e2eaf6;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s;
  min-width: 0;
}
.option-card input[type="radio"] {
  display: none;
}
.option-card:hover {
  border-color: #b3c8ee;
  background: #fafcff;
}
.option-card.selected {
  border-color: #003da5;
  background: #fafcff;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}

.option-card.disabled {
  opacity: 0.65;
  cursor: not-allowed;
  pointer-events: none;
}

.option-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 61, 165, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #003da5;
  flex-shrink: 0;
}
.option-text {
  flex: 1;
  min-width: 0;
}
.option-title {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: #0a2240;
  overflow-wrap: anywhere;
}
.option-sub {
  margin: 0;
  font-size: 12px;
  color: #5a6b82;
  overflow-wrap: anywhere;
}
.option-radio-col {
  flex-shrink: 0;
}

/* ── BILLING MODE ── */
.billing-mode-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.billing-mode-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1.5px solid #e2eaf6;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s;
}
.billing-mode-card input[type="radio"] {
  display: none;
}
.billing-mode-card:hover {
  border-color: #b3c8ee;
}
.billing-mode-card.selected {
  border-color: #003da5;
  background: #fafcff;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}
.billing-mode-card .radio-ring {
  flex-shrink: 0;
  margin-top: 2px;
}
.mode-text {
  flex: 1;
}

/* ── BILLING PANEL ── */
.billing-panel {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.billing-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.address-card {
  border: 1.5px solid #e2eaf6;
  border-radius: 14px;
  background: #fff;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.address-card:hover {
  border-color: #b3c8ee;
}
.address-card.selected {
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
  background: #fafcff;
}

/* ── SUMMARY ── */
.summary-items {
  margin: 14px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.summary-item {
  display: flex;
  align-items: center;
  gap: 14px;
}
.summary-item-img {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
}
.summary-item-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 20px;
}
.summary-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-item-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0a2240;
  white-space: normal;
  overflow-wrap: anywhere;
}
.summary-item-sku {
  margin: 0;
  font-size: 11px;
  color: #94a3b8;
}
.summary-item-qty {
  margin: 0;
  font-size: 12px;
  color: #5a6b82;
  font-weight: 600;
}
.summary-item-price {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #003da5;
  white-space: nowrap;
  flex-shrink: 0;
}

.summary-totals {
  border-top: 1px solid #edf2f7;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #344760;
}
.combo-discount-row {
  color: #15803d;
  font-weight: 600;
}
.combo-discount-row i {
  margin-right: 4px;
}
.delivery-row {
  margin-top: 2px;
}
.delivery-estimate {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #5f6368;
}
.delivery-estimate svg {
  flex-shrink: 0;
  color: #003da5;
}
.delivery-estimate strong {
  color: #0a2240;
  font-weight: 700;
}
.summary-divider {
  height: 1px;
  background: #edf2f7;
  margin: 4px 0;
}
.total-row.grand {
  padding-top: 10px;
  border-top: 1px solid #edf2f7;
  font-size: 16px;
  font-weight: 800;
  color: #0a2240;
}
.free-tag {
  color: #15803d;
  font-weight: 700;
  font-size: 13px;
}

/* ── CTA GROUP ── */
.cta-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cta-ghost {
  width: 100%;
  height: 44px;
  border: 1.5px solid #d1dbe8;
  border-radius: 50px;
  background: #fff;
  color: #344760;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  transition: all 0.2s;
  font-family: inherit;
  box-sizing: border-box;
}
.cta-ghost:hover {
  border-color: #003da5;
  color: #003da5;
}

.cta-ghost.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.cta-primary {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 50px;
  color: #0a2240;
  background: #ffc72c;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  transition: all 0.2s;
  box-sizing: border-box;
}
.cta-primary:hover:not(:disabled) {
  background: #0033a0;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 61, 165, 0.22);
}
.cta-razorpay {
  background: #0033a0;
  color: #fff;
}
.cta-razorpay:hover:not(:disabled) {
  background: #ffc72c;
  color: #000;
}
.cta-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── SHARED ── */
.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: #fff;
  color: #003da5;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.ghost-btn:hover {
  background: #003da5;
  color: #fff;
}

.add-addr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #003da5;
  background: transparent;
  color: #003da5;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.add-addr-btn:hover {
  background: #003da5;
  color: #fff;
}

.status-text {
  margin: 0;
  color: #5f6f85;
  font-size: 14px;
}
.status-text.centered {
  text-align: center;
  padding: 40px 0;
}
.status-text.error {
  color: #dc2626;
  font-weight: 700;
}

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .checkout-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
@media (max-width: 768px) {
  .payment-content {
    padding: 24px 0 48px;
    gap: 18px;
  }
  .checkout-grid {
    gap: 14px;
  }
  .panel-card,
  .summary-card {
    padding: 16px;
    border-radius: 14px;
  }
  .option-card {
    padding: 12px;
    gap: 10px;
    align-items: flex-start;
  }
  .option-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    font-size: 18px;
  }
  .option-radio-col {
    align-self: center;
  }
  .summary-item {
    align-items: flex-start;
  }
  .summary-item-img {
    width: 54px;
    height: 54px;
  }
  .cta-ghost {
    height: 42px;
    font-size: 13px;
  }
  .cta-primary {
    height: 48px;
    font-size: 14px;
    padding: 0 12px;
    text-align: center;
  }
}
@media (max-width: 600px) {
  .progress-line {
    width: 48px;
  }
  .step-dot {
    width: 34px;
    height: 34px;
    font-size: 14px;
  }
  .step-label {
    font-size: 11px;
  }
}
@media (max-width: 420px) {
  .container {
    width: 94%;
  }
  .progress-line {
    width: 30px;
    margin-bottom: 16px;
  }
  .payment-content {
    gap: 14px;
  }
  .option-sub {
    font-size: 11px;
  }
  .summary-item-sku,
  .summary-item-qty {
    font-size: 11px;
  }
}
</style>
